import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { setCredentials, setUser, logout as logoutAction } from '../store/authSlice';
import api from '../services/api';

export function useAuth() {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated, loading } = useSelector((s: RootState) => s.auth);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const r = await api.login({ email, password });
      if (r.data?.mfaRequired) return { mfaRequired: true, mfaToken: r.data.mfaToken };
      const { access_token, user: userData } = r.data;
      dispatch(setCredentials({ user: userData, token: access_token }));
      return userData;
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Invalid credentials. Please try again.';
      setError(msg);
      return { error: msg };
    }
  }, [dispatch]);

  const loadUser = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api.me();
      dispatch(setUser(r.data));
    } catch {
      dispatch(logoutAction());
    }
  }, [dispatch, token]);

  // Full page reload on logout — guarantees Redux + memory + cached state are all cleared.
  // The server-side call is fire-and-forget (records the LOGOUT audit event / invalidates
  // the session record) — it must never block the client from actually logging out.
  const logout = useCallback(() => {
    api.logout().catch(() => {});
    dispatch(logoutAction());
    localStorage.clear();
    window.location.replace('/login');
  }, [dispatch]);

  const clearError = useCallback(() => setError(null), []);

  return { user, token, isAuthenticated, loading, error, login, logout, loadUser, clearError };
}